import './index.less';
import * as React from 'react';
import PropTypes from 'prop-types';
import { Button, Table, InputNumber, Icon, Form, Input } from '@beehive/components';
import { TabContent } from '../components';
import { isBlank, showAlert } from 'utils';
import classnames from 'classnames';
import { financial } from 'utils/number';
import _ from 'lodash';
import messages from 'messages/Workbench/ProjectDetail';
import { getCooperateAccountsQuoteData, getTranslatedFormItem } from '../../../../utils';
import { tableColumnWidthToRem } from '@mpc/common/src/utils';

export interface PendingQuoteProps {
  tabKey: string;
}

const TABLE_COLUMNS = [
  { key: 'index', title: 'index', align: 'left', width: 120 },
  { key: 'platform', title: 'superior_resource_id', align: 'center', width: 120 },
  { key: 'account', title: 'account', align: 'left', width: 120 },
  { key: 'remark', title: 'remark', align: 'left', width: 120 },
  { key: 'quote_price_origin', title: 'quote_price_origin', align: 'right', width: 203 },
  { key: 'arrow', title: '', align: 'center', width: 64 },
  { key: 'quote_price', title: 'quote_price_changed', align: 'left', width: 203 },
];
const FULL_COL_PROPS = {
  itemColProps: { span: 24 },
  itemLabelColProps: { span: 4 },
  itemInputColProps: { span: 20 },
};
export const FormProps = {
  mode: 'edit',
  isLabelVertical: false,
  showClientError: false,
  checkMinHeight: false,
  ...FULL_COL_PROPS,
};
const FormItems: any[] = [
  {
    key: 'remark',
    label: 'requoteNote',
    childProps: {
      type: 'textArea',
      maxLength: 800,
      maxHeight: 76,
      minHeight: 76,
      autosize: true,
    },
  },
];

class PendingQuote extends React.Component<PendingQuoteProps, any> {

  public static contextTypes = {
    intl: PropTypes.object,
    prefixCls: PropTypes.string,
    menuKey: PropTypes.string,
    detail: PropTypes.any,

    loadDetail: PropTypes.func,
    projectMove: PropTypes.func,
  };

  private tableRef: React.RefObject<typeof Table> = React.createRef();

  constructor(props: PendingQuoteProps, context: any) {
    super(props, context);

    const { intl, detail } = context;
    const data = getCooperateAccountsQuoteData(detail, intl, messages);
    const form = data.filter((i: any) => i.is_win || i.isTotal).map((i: any, index: number) => {
      return { ...i, index: index + 1, quote_price_origin: i.quote_price };
    });

    this.state = {
      form,
      formExtra: {},
      hasChanged: false,
      submitting: false,
    };
  }

  private handleSubmit = () => {
    const { menuKey, detail, loadDetail, projectMove } = this.context;
    const { form = [], formExtra = {} } = this.state;
    const params = form.filter((i: any) => !i.isTotal).filter((i: any) => !isBlank(i.quote_price));
    if (this.tableRef.current && !this.tableRef.current.valid()) {
      return;
    }
    this.setState({ submitting: true });
    return projectMove(
      {
        id: detail.id,
        making: {
          is_pass: false,
          ...formExtra,
          project_suppliers_attributes: {
            id: detail.current_project_supplier_id,
            project_supplier_quotes_attributes: params.map((i: any) => {
              return { id: i.quote_id, requote_price: i.quote_price };
            }),
          },
        },
      },
      { menuKey },
    ).then((res: any) => {
      showAlert({ type: 'success', message: res.res_msg });
      this.setState({ submitting: false });
      loadDetail();
    }).catch((error: any) => {
      showAlert({ type: 'error', message: typeof error === 'string' ? error : error.message });
      this.setState({ submitting: false });
    });
  }

  private getTotalPrice = (data: any[]) => {
    const total = data.filter((i: any) => !i.isTotal)
      .reduce((prev: number, curr: any) => {
        const price = curr['quote_price'];
        prev = _.add(prev, isBlank(price) ? 0 : +price);
        return prev;
      }, 0);
    return total;
  }

  private handleChange = (key: string, index: number, value: any) => {
    const { form = [] } = this.state;
    const newForm = [...form];
    newForm[index][key] = value;
    newForm[newForm.length - 1]['quote_price'] = this.getTotalPrice(newForm);
    const hasChanged = newForm.some((i: any) => i.quote_price !== i.quote_price_origin);
    this.setState({ form: newForm, hasChanged });
  }

  private handleFormChange = (key: string, value: any) => {
    const { formExtra = {} } = this.state;
    const newForm = { ...formExtra };
    newForm[key] = value;
    this.setState({ formExtra: newForm });
  }

  private renderFormItemChild = (itemProps: any, childProps: any) => {
    const { formExtra = {} } = this.state;
    const { key } = itemProps;
    const { type, ...childPropsRest } = childProps;
    switch (type) {
      case 'textArea': {
        return (
          <Input.TextArea
            {...childPropsRest}
            value={isBlank(formExtra[key]) ? '' : formExtra[key]}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              this.handleFormChange(key, evt.target.value);
            }}
          />
        );
      }
      default: {
        return <span />;
      }
    }
  }

  private getColumnDefaultRender = () => {
    return (v: any, r: any, i: number) => {
      return isBlank(v) ? r.isTotal ? '' : '--' : v;
    };
  }

  private getColumnRender = () => {
    const { intl } = this.context;
    const { hasChanged } = this.state;
    return {
      index: (v: any, r: any, i: number) => {
        return r.isTotal ? intl.formatMessage(messages.quote_price_total) : isBlank(v) ? '--' : v;
      },
      quote_price_origin: (v: any, r: any, i: number) => {
        return isBlank(v) ? '--' : financial(v);
      },
      arrow: (v: any, r: any, i: number) => {
        let isChanged = r['quote_price_origin'] !== r['quote_price'];
        if (r.isTotal) {
          isChanged = hasChanged;
        }
        return isChanged ? <Icon name="arrow-up" /> : '';
      },
      quote_price: (v: any, r: any, i: number) => {
        if (r.isTotal) {
          return isBlank(v) ? '--' : (
            <span className={hasChanged ? 'quote-changed' : ''}>{financial(v)}</span>
          );
        }
        return (
          <InputNumber
            hideHandle
            min={0}
            precision={2}
            placeholder={intl.formatMessage(messages.inputPlaceholder)}
            value={v}
            formatter={(val: any) => financial(val, true)}
            onChange={(value: any) => this.handleChange('quote_price', i, value)}
          />
        );
      },
    };
  }

  private getColumns = () => {
    const { intl } = this.context;
    const defaultRender = this.getColumnDefaultRender();
    const columRenders = this.getColumnRender();
    return TABLE_COLUMNS.map((item: any) => {
      const title = !isBlank(item.title) ? intl.formatMessage(messages[item.title]) : '';
      return {
        ...tableColumnWidthToRem(item),
        dataIndex: item.key,
        title,
        name: title,
        render: columRenders[item.key] || defaultRender,
        required: item.key === 'quote_price',
        className: classnames({
          [item.key]: true,
        }),
      };
    });
  }

  private renderBottom = () => {
    const { intl } = this.context;
    const { hasChanged, submitting } = this.state;
    return (
      <Button
        className="pull-right"
        size="large"
        type="primary"
        disabled={!hasChanged}
        loading={submitting}
        onClick={this.handleSubmit}
      >
        {intl.formatMessage(messages.submit)}
      </Button>
    );
  }

  public render() {
    const { intl } = this.context;
    const { tabKey } = this.props;
    const { form } = this.state;

    const bottom = this.renderBottom();
    const columns = this.getColumns();

    return (
      <TabContent tabKey={tabKey} bottom={bottom} canScroll>
        <Table
          ref={this.tableRef}
          columns={columns}
          data={form}
          size="small"
          rowKey="index"
        />
        <Form {...FormProps}>
          {FormItems.map((item: any) => {
            const translatedItem = getTranslatedFormItem(item, intl, messages);
            const { childProps, ...itemProps } = translatedItem;
            return (
              <Form.Item
                {...itemProps}
                key={itemProps.key}
                itemKey={itemProps.formKey || itemProps.key}
                className={itemProps.key}
              >
                {this.renderFormItemChild(itemProps, childProps)}
              </Form.Item>
            );
          })}
        </Form>
      </TabContent>
    );
  }

}

export default PendingQuote;
